import { type FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import axios from 'axios';

import ModalWindow from '@editor2/components/ModalWindow';
import { type RootState } from '@editor2/reducers/root';
import { type MCQuestionPool } from '@editor2/types/pageElements';
import { feedbackModalStyles } from './styles';
import { clearQuestionGenerationData } from '@editor2/reducers/editor/editorSlice';

const FEEDBACK_FIELDS = [
	{
		key: 'item_alignment',
		label: 'item alignment',
		tooltip:
			'How well the generated pool items align to the learning objective, as well as how consistent those items are across the pool'
	},
	{
		key: 'distractor_plausibility',
		label: 'distractor plausibility',
		tooltip: 'How plausible the generated distractor choices are'
	},
	{
		key: 'distractor_rejoinders',
		label: 'distractor rejoinders',
		tooltip: 'The overall quality of the rejoinders of distractor choices'
	},
	{
		key: 'adherence_to_guidelines',
		label: 'adherence to guidelines',
		tooltip: 'How well the generated content adheres to structural & style guidelines'
	},
	{ key: 'overall_readability', label: 'overall readability', tooltip: '' }
];

const FEEDBACK_OPTIONS = [
	{ value: 1, emoji: '😠' },
	{ value: 2, emoji: '😕' },
	{ value: 3, emoji: '😐' },
	{ value: 4, emoji: '😊' },
	{ value: 5, emoji: '🥳' }
];

interface Props {
	pageId: string;
	index?: number;
	isShown: boolean;
	courseId: number;
	onHide: () => void;
	element: MCQuestionPool;
}

const QuestionFeedbackModal: FC<Props> = (props) => {
	const { pageId, isShown, courseId, onHide, element } = props;

	const { pages } = useSelector((state: RootState) => state.courses[courseId]);

	const [feedback, setFeedback] = useState<{ [key: string]: number }>({});
	const [error, setError] = useState<string | null>(null);

	const handleChange = (field: string, value: number) => {
		setFeedback((prev) => ({ ...prev, [field]: value }));
	};

	const submitFeedback = async () => {
		try {
			await axios.post('/editor2/ai_feedback', {
				feedback: {
					rating: feedback,
					input: element.questionGenerationPayload,
					output: element.questionGenerationResponse
				},
				course_id: courseId,
				page_family_id: pages.entities[pageId].family_id,
				mcqp_family_id: element.family_id
			});
			setError(null);
			onHide();
		} catch (e) {
			setError(
				'Recording your feedback failed. You can try again, skip, or reach out to tech support.'
			);
		}
	};

	const footer = (
		<>
			{error && (
				<div
					style={{
						color: 'red',
						marginBottom: '0.5rem',
						textAlign: 'center'
					}}>
					{error}
				</div>
			)}
			<Button
				variant="success"
				size="sm"
				onClick={submitFeedback}
				disabled={FEEDBACK_FIELDS.some((f) => !feedback[f.key])}>
				Submit
			</Button>
			<Button variant="secondary" size="sm" onClick={onHide}>
				Skip Feedback
			</Button>
		</>
	);

	return (
		<ModalWindow
			isShown={isShown}
			onHide={onHide}
			title={<div>Send Question Generation Feedback</div>}
			footer={footer}
			modalStyles={feedbackModalStyles}>
			{/* MC Question Pool Preview */}
			<div className="mb-4">
				{element.questions.map((question, qIdx) => (
					<div key={question.family_id} className="mb-3">
						<div>
							<strong>Q{qIdx + 1}:</strong> {question.body}
						</div>
						<ul className="list-unstyled ms-3">
							{question.choices.map((choice, cIdx) => (
								<li key={choice.family_id} className="mb-2">
									<div className="d-flex align-items-center">
										<Form.Check
											type="radio"
											disabled
											name={`preview-q${qIdx}`}
											id={`preview-q${qIdx}-c${cIdx}`}
											checked={choice.is_correct}
											className="me-2"
										/>
										<span>
											{choice.body}
											{choice.is_correct && (
												<span className="ms-3 badge bg-success correct-badge">Correct</span>
											)}
										</span>
									</div>
									{choice.rejoinder && (
										<div className="ms-4 text-muted fst-italic rejoinder">{choice.rejoinder}</div>
									)}
								</li>
							))}
						</ul>
					</div>
				))}
			</div>
			{/* Feedback Form */}
			<Form>
				<table className="table table-bordered text-center feedback-table">
					<thead>
						<tr>
							<th className="feedback-th"></th>
							{FEEDBACK_OPTIONS.map((opt) => (
								<th key={opt.value}>
									{opt.value} – {opt.emoji}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{FEEDBACK_FIELDS.map((field) => (
							<tr key={field.key}>
								<td className="feedback-label-cell">
									<span className="feedback-label-flex">
										{field.label}
										{field.tooltip && (
											<OverlayTrigger
												placement="top"
												overlay={<Tooltip id={`tooltip-${field.key}`}>{field.tooltip}</Tooltip>}>
												<span className="info-icon" tabIndex={0} aria-label="More info">
													{'ℹ️'}
												</span>
											</OverlayTrigger>
										)}
									</span>
								</td>
								{FEEDBACK_OPTIONS.map((opt) => (
									<td key={opt.value}>
										<Form.Check
											type="radio"
											name={field.key}
											id={`${field.key}-${opt.value}`}
											checked={feedback[field.key] === opt.value}
											onChange={() => handleChange(field.key, opt.value)}
										/>
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</Form>
		</ModalWindow>
	);
};

export default QuestionFeedbackModal;
